import React from "react";
import "./AdvScheduleGraph3.css";
import { recipeColorOf, recipeNameOf, NO_RECIPE_GRAY } from "./advScheduleUtils";
import { cl, globs } from "../../../components/utils/utils";

const DAY = 1440;
const SNAP_MIN = 5;
const MIN_RUN = 5;
const ZOOMS = [24, 36, 48, 72, 96, 144];
const CONFLICT = "__conflict__";

function inkOn(hex) {
  if (!hex || hex[0] !== "#") return "#1a2332";
  const h = hex.slice(1);
  if (h.length < 6) return "#fff";
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const y = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  return y > 0.62 ? "#1a2332" : "#fff";
}

function fmtTime(mins) {
  const m = ((mins % DAY) + DAY) % DAY;
  const h = Math.floor(m / 60);
  const mm = m % 60;
  const ap = h < 12 ? "AM" : "PM";
  const h12 = h % 12 || 12;
  return `${h12}:${String(mm).padStart(2, "0")} ${ap}`;
}

function shortHour(h) {
  if (h === 0) return "12 AM";
  if (h === 12) return "12 PM";
  return h < 12 ? `${h} AM` : `${h - 12} PM`;
}

/** Minute owners from recipe segments. A segment with an end fills that
 *  range. Segments with only a start hold until the next one, wrapping the day. */
export function recipeOwners(segments) {
  const owners = new Array(DAY).fill(null);
  const list = (segments || []).filter((s) => s && s.start != null);
  if (!list.length) return owners;
  const hasEnd = list.some((s) => s.end != null);
  if (hasEnd) {
    list.forEach((s) => {
      if (s.type === "empty") return;
      const id = s.type === "conflict" ? CONFLICT : s.recipeId;
      if (!id) return;
      const from = Math.max(0, Math.floor(Number(s.start) || 0));
      const to = Math.min(DAY, Math.floor(Number(s.end)));
      for (let m = from; m < to; m++) owners[m] = id;
    });
    return owners;
  }
  const pts = list
    .filter((s) => s.recipeId)
    .slice()
    .sort((a, b) => a.start - b.start);
  if (!pts.length) return owners;
  for (let k = 0; k < pts.length; k++) {
    const from = ((Math.floor(pts[k].start) % DAY) + DAY) % DAY;
    const next = k + 1 < pts.length ? Math.floor(pts[k + 1].start) : Math.floor(pts[0].start) + DAY;
    for (let m = from; m < next; m++) owners[((m % DAY) + DAY) % DAY] = pts[k].recipeId;
  }
  return owners;
}

function runFacts(start, dur, owners) {
  const len = Math.max(0, Math.round(Number(dur) || 0));
  const segs = [];
  let sawNone = false;
  const recipes = [];
  for (let k = 0; k < len; k++) {
    const o = owners[(start + k) % DAY];
    if (segs.length && segs[segs.length - 1].own === o) segs[segs.length - 1].len += 1;
    else segs.push({ at: k, len: 1, own: o });
    if (o == null) sawNone = true;
    else if (recipes.indexOf(o) < 0) recipes.push(o);
  }
  return { start, len, segs, sawNone, recipes };
}

function colourOf(recipes, id) {
  if (id === CONFLICT) return "#e53935";
  return recipeColorOf(recipes, id) || NO_RECIPE_GRAY;
}

function nameOf(recipes, id) {
  if (id === CONFLICT) return "Conflict";
  if (!id) return "No recipe";
  return recipeNameOf(recipes, id);
}

function snapMin(m) {
  return Math.round(m / SNAP_MIN) * SNAP_MIN;
}

function neighbourBounds(run, others) {
  let prevEnd = 0;
  let nextStart = DAY;
  (others || []).forEach((o) => {
    if (o.start + o.dur <= run.start) prevEnd = Math.max(prevEnd, o.start + o.dur);
    else if (o.start >= run.start + run.dur) nextStart = Math.min(nextStart, o.start);
  });
  return { prevEnd, nextStart };
}

function dragResult(mode, run, others, deltaMin) {
  const b = neighbourBounds(run, others);
  const end = run.start + run.dur;
  if (mode === "left") {
    let start = snapMin(run.start + deltaMin);
    start = Math.max(b.prevEnd, Math.min(start, end - MIN_RUN));
    return { start, dur: end - start };
  }
  if (mode === "right") {
    let e = snapMin(end + deltaMin);
    e = Math.min(b.nextStart, Math.max(e, run.start + MIN_RUN));
    return { start: run.start, dur: e - run.start };
  }
  let start = snapMin(run.start + deltaMin);
  start = Math.max(b.prevEnd, Math.min(start, b.nextStart - run.dur));
  return { start, dur: run.dur };
}

function eventReadout(valve, ev, owners, recipes) {
  const f = runFacts(ev.dayMins, ev.durMins, owners);
  const end = (Number(ev.dayMins) + f.len) % DAY;
  const parts = f.recipes.map((id) => nameOf(recipes, id));
  if (f.sawNone) parts.push("no window");
  const into = parts.join(" + ") || "no window";
  return `${valve.name || valve.id}  ${fmtTime(ev.dayMins)} to ${fmtTime(end)}, ${f.len} min, into ${into}`;
}

function findingsFor(valve, owners, dragId, dragAt) {
  const out = [];
  (valve.events || []).forEach((ev, n) => {
    const run = placedRun(valve, ev, n, dragId, dragAt);
    const f = runFacts(run.start, run.dur, owners);
    if (f.recipes.length > 1) {
      out.push({
        n: n + 1,
        kind: "split",
        text: `This ${f.len} minute run spans ${f.recipes.length} recipes, so the same watering delivers more than one feed.`,
      });
    }
    if (f.sawNone) {
      out.push({
        n: n + 1,
        kind: "dry",
        text: f.recipes.length
          ? "Part of this run falls where no recipe is held, so that part is source water."
          : `No recipe is held for any of this run, so the whole ${f.len} minutes is source water.`,
      });
    }
  });
  return out;
}

const CHANGEOVER_MIN = 5;

function fmtClock(mins) {
  const m = ((mins % DAY) + DAY) % DAY;
  const h = Math.floor(m / 60);
  const mm = m % 60;
  const ap = h < 12 ? "AM" : "PM";
  const h12 = h % 12 || 12;
  return `${h12 < 10 ? "0" : ""}${h12}:${String(mm).padStart(2, "0")} ${ap}`;
}

function placedRun(valve, ev, index, dragId, dragAt) {
  let start = Math.round(Number(ev.dayMins) || 0);
  let dur = Math.max(0, Math.round(Number(ev.durMins) || 0));
  if (dragId === `${valve.id}:${index}` && dragAt) {
    start = dragAt.start;
    dur = dragAt.dur;
  }
  return {
    valveId: valve.id,
    zone: valve.zone || "",
    name: valve.name || valve.id,
    index,
    start,
    dur,
    end: start + dur,
    recipe: ev.recipe || "",
  };
}

function collectRuns(valves, dragId, dragAt) {
  const runs = [];
  (valves || []).forEach((v) => {
    (v.events || []).forEach((ev, index) => {
      const run = placedRun(v, ev, index, dragId, dragAt);
      if (run.dur > 0) runs.push(run);
    });
  });
  return runs;
}

function intervalsOf(run) {
  if (run.end <= DAY) return [[run.start, run.end]];
  return [[run.start, DAY], [0, run.end - DAY]];
}

function overlapWindow(a, b) {
  const ia = intervalsOf(a);
  const ib = intervalsOf(b);
  for (let i = 0; i < ia.length; i++) {
    for (let j = 0; j < ib.length; j++) {
      const lo = Math.max(ia[i][0], ib[j][0]);
      const hi = Math.min(ia[i][1], ib[j][1]);
      if (hi > lo) return [lo, hi];
    }
  }
  return null;
}

function gapMin(a, b) {
  let best = Infinity;
  [[a, b], [b, a]].forEach(([first, second]) => {
    let g = second.start - first.end;
    if (g < 0) g += DAY;
    if (g < best) best = g;
  });
  return best;
}

/** Two valves open together must want the same recipe. A run with no recipe
 *  asks the skid for nothing, so it is left out of the pair. */
function checkSchedule(valves, dragId, dragAt) {
  const runs = collectRuns(valves, dragId, dragAt);
  const conflicts = [];
  const warnings = [];
  for (let i = 0; i < runs.length; i++) {
    for (let j = i + 1; j < runs.length; j++) {
      const a = runs[i];
      const b = runs[j];
      if (a.valveId === b.valveId) continue;
      if (!a.recipe || !b.recipe || a.recipe === b.recipe) continue;
      const win = overlapWindow(a, b);
      if (win) {
        conflicts.push({ kind: "conflict", a, b, from: win[0], to: win[1] });
      } else {
        const g = gapMin(a, b);
        if (g < CHANGEOVER_MIN) {
          warnings.push({ kind: "no-time", a, b, gapMin: g, needMin: CHANGEOVER_MIN });
        }
      }
    }
  }
  return { conflicts, warnings };
}

function unreadCount(owners, valves, dragId, dragAt) {
  const open = new Array(DAY).fill(false);
  collectRuns(valves, dragId, dragAt).forEach((r) => {
    const span = Math.min(r.dur, DAY);
    for (let k = 0; k < span; k++) open[(r.start + k) % DAY] = true;
  });
  const owns = {};
  const drank = {};
  for (let m = 0; m < DAY; m++) {
    const id = owners[m];
    if (id == null) continue;
    owns[id] = true;
    if (open[m]) drank[id] = true;
  }
  return Object.keys(owns).filter((id) => !drank[id]).length;
}

function who(run) {
  return `${run.zone || "—"} · ${run.name}`;
}

function checkTitle(nC, nW) {
  if (nC) {
    const conflicts = nC === 1 ? "1 recipe conflict" : `${nC} recipe conflicts`;
    if (!nW) return conflicts;
    return `${conflicts} and ${nW} ${nW === 1 ? "warning" : "warnings"}`;
  }
  return nW === 1 ? "1 warning" : `${nW} warnings`;
}

function GridLines({ slotW, hour }) {
  const lines = [];
  for (let h = 0; h < 24; h++) {
    lines.push(
      <i
        key={`h${h}`}
        className={hour ? "nab__gl nab__gl--hour" : "nab__gl"}
        style={{ left: h * slotW * 2 }}
      />
    );
    if (!hour) {
      lines.push(
        <i key={`m${h}`} className="nab__gl nab__gl--half" style={{ left: h * slotW * 2 + slotW }} />
      );
    }
  }
  return lines;
}

export default class AdvScheduleGraph3 extends React.Component {
  componentDidMount() {
    this.scrollToNow();
  }

  scrollToNow = () => {
    if (this.scrolled || this.props.nowMins == null) return;
    const sc = this.scrollRef.current;
    if (!sc || !sc.clientWidth) return;
    this.scrolled = true;
    const x = (this.props.nowMins / 30) * this.state.slotW;
    sc.scrollLeft = Math.max(0, x - (sc.clientWidth || 960) / 4);
  };

  constructor(props) {
    super(props);
    this.state = {
      slotW: 48,
      audit: false,
      nameQuery: "",
      zones: null,
      zoneOpen: false,
      sortKey: "zone",
      sortDir: 1,
      readout: "",
    };
    this.scrollRef = React.createRef();
  }

  componentWillUnmount() {
    window.removeEventListener("mousemove", this.onDragMove);
    window.removeEventListener("mouseup", this.onDragUp);
  }

  /** Top row first. A block keeps its recipe; for each minute the highest
   *  block that has one overwrites the schedule on the top bar. */
  blockOwners = (dragId, dragAt) => {
    const owners = recipeOwners(this.props.segments);
    const list = this.visibleValves();
    for (let r = list.length - 1; r >= 0; r--) {
      const v = list[r];
      (v.events || []).forEach((ev, index) => {
        if (!ev.recipe) return;
        let start = Math.round(Number(ev.dayMins) || 0);
        let dur = Math.max(0, Math.round(Number(ev.durMins) || 0));
        if (dragId === `${v.id}:${index}` && dragAt) {
          start = dragAt.start;
          dur = dragAt.dur;
        }
        for (let k = 0; k < dur; k++) owners[(start + k) % DAY] = ev.recipe;
      });
    }
    return owners;
  };

  onDragMove = (e) => {
    const d = this.drag;
    if (!d) return;
    const deltaMin = ((e.clientX - d.x0) / this.state.slotW) * 30;
    d.at = dragResult(d.mode, d.run, d.others, deltaMin);
    const owners = this.blockOwners(d.id, d.at);
    this.setState({
      dragId: d.id,
      dragAt: d.at,
      readout: eventReadout(
        d.valve,
        { dayMins: d.at.start, durMins: d.at.dur },
        owners,
        d.recipes
      ),
    });
  };

  onDragUp = () => {
    window.removeEventListener("mousemove", this.onDragMove);
    window.removeEventListener("mouseup", this.onDragUp);
    const d = this.drag;
    this.drag = null;
    if (
      d &&
      this.props.onEditRun &&
      (d.at.start !== d.run.start || d.at.dur !== d.run.dur)
    ) {
      this.props.onEditRun({
        valve: d.valve,
        index: d.index,
        dayMins: d.at.start,
        durMins: d.at.dur,
      });
    }
    this.setState({ readout: "", dragId: null, dragAt: null });
  };

  beginDrag = (e, valve, ev, index, owners, recipes) => {
    if (e.button !== 0) return;
    e.preventDefault();
    const start = Math.round(Number(ev.dayMins) || 0);
    const dur = Math.max(0, Math.round(Number(ev.durMins) || 0));
    const handle = e.target && e.target.dataset ? e.target.dataset.h : "";
    const mode = handle === "right" ? "right" : handle === "left" ? "left" : "move";
    window.removeEventListener("mousemove", this.onDragMove);
    window.removeEventListener("mouseup", this.onDragUp);
    if (dur <= 0 || start + dur > DAY) {
      this.drag = null;
      this.setState({ readout: eventReadout(valve, ev, owners, recipes) });
      const up = () => {
        window.removeEventListener("mouseup", up);
        this.setState({ readout: "" });
      };
      window.addEventListener("mouseup", up);
      return;
    }
    const others = (valve.events || [])
      .map((o, i) => ({
        i,
        start: Math.round(Number(o.dayMins) || 0),
        dur: Math.max(0, Math.round(Number(o.durMins) || 0)),
      }))
      .filter((o) => o.i !== index && o.dur > 0)
      .map((o) => ({ start: o.start, dur: o.dur }));
    this.drag = {
      id: `${valve.id}:${index}`,
      valve,
      index,
      mode,
      x0: e.clientX,
      run: { start, dur },
      at: { start, dur },
      others,
      owners,
      recipes,
    };
    window.addEventListener("mousemove", this.onDragMove);
    window.addEventListener("mouseup", this.onDragUp);
    this.setState({
      dragId: this.drag.id,
      dragAt: this.drag.at,
      readout: eventReadout(valve, ev, owners, recipes),
    });
  };

  px = (mins) => (mins / 30) * this.state.slotW;

  setZoom = (slotW) => {
    const i = ZOOMS.indexOf(slotW);
    if (i < 0) return;
    this.setState({ slotW });
  };

  stepZoom = (dir) => {
    const i = ZOOMS.indexOf(this.state.slotW);
    const n = Math.max(0, Math.min(ZOOMS.length - 1, i + dir));
    this.setState({ slotW: ZOOMS[n] });
  };

  toggleSort = (key) => {
    this.setState((s) => ({
      sortKey: key,
      sortDir: s.sortKey === key ? -s.sortDir : 1,
    }));
  };

  zoneNames = () => {
    const names = [];
    (this.props.valves || []).forEach((v) => {
      const z = v.zone || "—";
      if (names.indexOf(z) < 0) names.push(z);
    });
    return names;
  };

  visibleValves = () => {
    const { valves } = this.props;
    const { zones, nameQuery, sortKey, sortDir } = this.state;
    const q = nameQuery.trim().toLowerCase();
    let list = (valves || []).filter((v) => {
      if (zones && zones.indexOf(v.zone || "—") < 0) return false;
      if (q && String(v.name || "").toLowerCase().indexOf(q) < 0) return false;
      return true;
    });
    const orig = new Map((valves || []).map((v, i) => [v.id, i]));
    list = list.slice().sort((a, b) => {
      const av = sortKey === "name" ? a.name || "" : a.zone || "";
      const bv = sortKey === "name" ? b.name || "" : b.zone || "";
      const c = String(av).localeCompare(String(bv));
      if (c) return c * sortDir;
      return (orig.get(a.id) || 0) - (orig.get(b.id) || 0);
    });
    return list;
  };

  onPanDown = (e) => {
    if (e.button !== 2) return;
    const sc = this.scrollRef.current;
    if (!sc) return;
    e.preventDefault();
    const startX = e.clientX;
    const startLeft = sc.scrollLeft;
    const move = (ev) => {
      sc.scrollLeft = startLeft - (ev.clientX - startX);
    };
    const up = () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  };

  renderMaster(owners, recipes, dayW) {
    const { audit } = this.state;
    const bands = [];
    let m = 0;
    while (m < DAY) {
      const id = owners[m];
      let n = m;
      while (n < DAY && owners[n] === id) n += 1;
      bands.push({ id, from: m, to: n });
      m = n;
    }
    const open = new Array(DAY).fill(false);
    (this.props.valves || []).forEach((v) => {
      (v.events || []).forEach((ev) => {
        const len = Math.max(0, Math.round(ev.durMins || 0));
        for (let k = 0; k < len; k++) open[(ev.dayMins + k) % DAY] = true;
      });
    });
    return (
      <div className="nab__master" style={{ width: dayW }}>
        <GridLines slotW={this.state.slotW} />
        {bands.map((b) => {
          const w = this.px(b.to - b.from);
          if (b.id == null) {
            if (b.to - b.from < 8) return null;
            return (
              <div
                key={`g${b.from}`}
                className="nab__gap"
                style={{ left: this.px(b.from), width: Math.max(2, w - 2) }}
                title={`No recipe, ${fmtTime(b.from)} to ${fmtTime(b.to)}`}
              />
            );
          }
          let unread = false;
          if (audit) {
            unread = true;
            for (let t = b.from; t < b.to; t++) if (open[t]) unread = false;
          }
          const colour = colourOf(recipes, b.id);
          const name = nameOf(recipes, b.id);
          return (
            <div
              key={`w${b.from}`}
              className={`nab__win${unread ? " is-flagged" : ""}`}
              style={{
                left: this.px(b.from) + 1,
                width: Math.max(3, w - 2),
                background: colour,
                color: inkOn(colour),
              }}
              title={`${name}, held ${fmtTime(b.from)} to ${fmtTime(b.to)}`}
            >
              <span className="nab__winnm">{name}</span>
            </div>
          );
        })}
      </div>
    );
  }

  renderBlock(valve, ev, index, owners, recipes) {
    const live = this.state.dragId === `${valve.id}:${index}` && this.state.dragAt;
    if (live) {
      ev = { ...ev, dayMins: this.state.dragAt.start, durMins: this.state.dragAt.dur };
    }
    const f = runFacts(ev.dayMins, ev.durMins, owners);
    if (f.len <= 0) return null;
    const notes = [];
    if (this.state.audit && f.recipes.length > 1) notes.push("split");
    if (this.state.audit && f.sawNone) notes.push("dry");
    const carried = ev.recipe || "";
    const background = carried ? colourOf(recipes, carried) : "#9aa4b0";
    const ink = inkOn(background);
    const pieces =
      f.start + f.len <= DAY
        ? [{ from: f.start, len: f.len, wrap: "" }]
        : [
            { from: f.start, len: DAY - f.start, wrap: " is-wrapstart" },
            { from: 0, len: f.start + f.len - DAY, wrap: " is-wrapend" },
          ];
    const label = valve.windowed
      ? nameOf(recipes, carried)
      : `${f.len} min`;
    return pieces.map((piece, pi) => {
      const wide = this.px(piece.len);
      return (
        <div
          key={`${valve.id}-${index}-${pi}`}
          className={
            "nab__blk is-draggable" +
            (notes.length ? " is-flagged" : "") +
            (valve.windowed ? " is-window" : "") +
            (valve.windowed &&
            this.props.nowMins != null &&
            this.props.nowMins >= piece.from &&
            this.props.nowMins < piece.from + piece.len
              ? " is-in"
              : "") +
            piece.wrap
          }
          style={{
            left: this.px(piece.from) + 1,
            width: Math.max(3, wide - 2),
            background,
          }}
          title={`${valve.name}, start ${index + 1}, ${fmtTime(ev.dayMins)} for ${f.len} min`}
          onMouseDown={(e) => this.beginDrag(e, valve, ev, index, owners, recipes)}
          onDoubleClick={(e) => {
            e.stopPropagation();
            if (this.props.onOpenValve) this.props.onOpenValve(valve);
          }}
        >
          {wide > 62 ? (
            <span className="nab__blkt" style={{ color: carried ? ink : "#5b6572" }}>
              {label}
            </span>
          ) : null}
          {notes.length ? <span className="nab__blkmk">⚠</span> : null}
          {wide >= 20 ? (
            <>
              <i className="nab__blkh nab__blkh--l" data-h="left" />
              <i className="nab__blkh nab__blkh--r" data-h="right" />
            </>
          ) : null}
        </div>
      );
    });
  }

  enableAdvGraph=()=>{
    let {onChange}=this.props
//     cl("enableAdvGraph")
    onChange({
      type:"update",
      value:{mode:"advGraph"},
    })
    globs.events.publish("savePageEnable", true);
    onChange({ type: "change", value: this.savePage });
    this.setState({ editSchedule: true });
  }


  render() {
    const { recipes, stub, editedCount, mode } = this.props;
//     cl(this.props)
    const { slotW, audit, nameQuery, zones, zoneOpen, sortKey, sortDir, readout } = this.state;
    const { dragId, dragAt } = this.state;
    const owners = this.blockOwners(dragId, dragAt);
    const dayW = this.px(DAY);
    const list = this.visibleValves();
    const zoneNames = this.zoneNames();
    const zoomI = ZOOMS.indexOf(slotW);
    let splitN = 0;
    let dryN = 0;
    let unreadN = 0;
    let schedule = { conflicts: [], warnings: [] };
    if (audit) {
      (this.props.valves || []).forEach((v) => {
        findingsFor(v, owners, dragId, dragAt).forEach((f) => {
          if (f.kind === "split") splitN += 1;
          if (f.kind === "dry") dryN += 1;
        });
      });
      unreadN = unreadCount(owners, this.props.valves, dragId, dragAt);
      schedule = checkSchedule(this.props.valves, dragId, dragAt);
    }
    const nC = schedule.conflicts.length;
    const nW = schedule.warnings.length;
    const lookN = splitN + dryN + unreadN + nC + nW;
    const chips = [];
    if (nC) chips.push(`${nC}  ${nC === 1 ? "recipe conflict" : "recipe conflicts"}`);
    if (nW) chips.push(`${nW}  ${nW === 1 ? "changeover warning" : "changeover warnings"}`);
    if (splitN) chips.push(`${splitN}  ${splitN === 1 ? "run spans more than one recipe" : "runs span more than one recipe"}`);
    if (dryN) chips.push(`${dryN}  ${dryN === 1 ? "run draws water with no window open" : "runs draw water with no window open"}`);
    if (unreadN) chips.push(`${unreadN}  ${unreadN === 1 ? "download nothing drank" : "downloads nothing drank"}`);
    const legendIds = [];
    owners.forEach((id) => {
      if (id && legendIds.indexOf(id) < 0) legendIds.push(id);
    });
    const zoneLabel = !zones || zones.length === zoneNames.length
      ? "All zones"
      : zones.length === 1
        ? zones[0]
        : `${zones.length} zones`;

    return (
      <div className="pgm3">
        <div className="pgm__head">
          <div>
            <div className="pgm__titlerow">
              <div className="view__title">Nutrient alignment&nbsp;
              {mode!="advGraph"&&
                <button type="button" className="filled"
                style={{position:"relative",zIndex:5}}
                onClick={this.enableAdvGraph}
                >Enable</button>
              }
              </div>
            </div>
            <div className="pgm__lede">When each valve opens, against the recipe the skid is holding.</div>
          </div>
          <div className={`pgm__state${editedCount ? " is-dirty" : ""}`} role="status">
            {editedCount ? (
              <>
                <i className="pgm__dot" />
                <span>
                  {`Unsaved changes on ${editedCount} ${editedCount === 1 ? "valve" : "valves"}. Save sends them to the skid; Cancel puts the day back.`}
                </span>
              </>
            ) : (
              <>
                <span className="pgm__tick">✓</span>
                <span>Showing the saved program</span>
              </>
            )}
          </div>
        </div>

        {stub ? <div className="pgm__stub">{stub}</div> : null}

        <div className="nab__bar pgm__tools">
          <div className="pgm__zoom">
            <span className="pgm__toollbl">Zoom</span>
            <button type="button" className="pgm__zbtn" aria-label="Zoom out" disabled={zoomI <= 0} onClick={() => this.stepZoom(-1)}>−</button>
            <select
              className="nab__zoom"
              aria-label="Zoom"
              value={String(slotW)}
              onChange={(e) => this.setZoom(Number(e.target.value))}
            >
              {ZOOMS.map((w) => (
                <option key={w} value={w}>{Math.round((w / 48) * 100)}%</option>
              ))}
            </select>
            <button type="button" className="pgm__zbtn" aria-label="Zoom in" disabled={zoomI >= ZOOMS.length - 1} onClick={() => this.stepZoom(1)}>+</button>
          </div>
          <i className="pgm__sep" />
          <label className={`nab__audit${audit ? " is-on" : ""}`}>
            <input type="checkbox" checked={audit} onChange={(e) => this.setState({ audit: e.target.checked })} />
            <span>Audit</span>
          </label>
          {audit ? (
            <div className="nab__checks">
              {lookN ? chips.map((text) => (
                <span key={text} className="nab__chk">{`⚠  ${text}`}</span>
              )) : (
                <span className="nab__chk is-clean">0  nothing to look at on this day</span>
              )}
              <span className="nab__note">Advisory only. The grower decides.</span>
            </div>
          ) : null}
        </div>

        <details className="pgm__help">
          <summary>How to read this board</summary>
          <ul>
            <li>The top lane is the recipe the skid is holding. A band stays that colour until the next recipe takes over.</li>
            <li>Each row is a valve. A block is one watering, coloured by the recipe it drinks. A block in two colours crosses a recipe change. A hatched block is a window: the controller decides when to water inside it.</li>
            <li>Right-click and drag the timeline to pan it. Double-click a valve, or a block, to edit it.</li>
            <li>Audit marks a run that spans two recipes, a run that falls where no recipe is held, and two valves open together that want different recipes. None of these blocks a save.</li>
          </ul>
        </details>

        {audit && (schedule.conflicts.length || schedule.warnings.length) ? (
          <div className={`cflict${schedule.conflicts.length ? "" : " cflict--warn"}`}>
            <div className="cflict__h">
              <span className="cflict__mark">!</span>
              <span className="cflict__t">Schedule check — {checkTitle(schedule.conflicts.length, schedule.warnings.length)}</span>
              <span className="cflict__sub">The skid holds one recipe. Two valves open together must want the same one.</span>
            </div>
            <div className="cflict__body">
              {schedule.conflicts.concat(schedule.warnings).map((f) => {
                const isC = f.kind === "conflict";
                const txt = isC
                  ? `${who(f.a)} and ${who(f.b)} are both open from ${fmtClock(f.from)} to ${fmtClock(f.to)} and want different recipes.`
                  : `${who(f.a)} and ${who(f.b)} are ${f.gapMin === 0 ? "back to back" : `${f.gapMin} minutes apart`} on different recipes, and a changeover needs about ${f.needMin} minutes, so the second one waters late.`;
                return (
                  <div key={`${f.a.valveId}:${f.a.index}:${f.b.valveId}:${f.b.index}:${f.kind}`} className="cflict__f">
                    <span className={`cflict__tag${isC ? "" : " cflict__tag--warn"}`}>{isC ? "CONFLICT" : "NO TIME"}</span>
                    <div className="cflict__x">
                      <div>{txt}</div>
                      <div className="cflict__chips">
                        {[f.a, f.b].map((run) => (
                          <span key={run.valveId + run.index} className="cflict__chip">
                            <span>{`${run.name} · ${fmtClock(run.start)} to ${fmtClock(run.end)}`}</span>
                            <i className="cflict__dot" style={{ background: colourOf(recipes, run.recipe) }} />
                            <span>{nameOf(recipes, run.recipe)}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}

        {readout ? <div className="nab__dragread">{readout}</div> : null}

        <div className="nab">
          <div className="nab__grid">
            <div className="nab__head">
              <div className="nab__headmaster">
                <span className="nab__lbl">Nutrient availability</span>
              </div>
              <div className="nab__headcols">
                <div className="nab__col">
                  <button type="button" className={`nab__sort${sortKey === "zone" ? " is-sorted" : ""}`} onClick={() => this.toggleSort("zone")}>
                    <span>Zone</span><i>{sortKey === "zone" && sortDir < 0 ? "↓" : "↑"}</i>
                  </button>
                  <button
                    type="button"
                    className={`nab__zonebtn${zones ? " is-set" : ""}`}
                    onClick={() => this.setState({ zoneOpen: !zoneOpen })}
                  >
                    <span>{zoneLabel}</span><i>▾</i>
                  </button>
                  {zoneOpen ? (
                    <div className="nab__zonepop">
                      {zoneNames.map((z) => {
                        const on = !zones || zones.indexOf(z) >= 0;
                        return (
                          <label key={z}>
                            <input
                              type="checkbox"
                              checked={on}
                              onChange={() => {
                                const base = zones ? zones.slice() : zoneNames.slice();
                                const next = on ? base.filter((x) => x !== z) : base.concat([z]);
                                this.setState({
                                  zones: next.length === zoneNames.length ? null : next,
                                });
                              }}
                            />
                            {z}
                          </label>
                        );
                      })}
                      <button type="button" className="nab__zoneall" onClick={() => this.setState({ zones: null, zoneOpen: false })}>
                        All zones
                      </button>
                    </div>
                  ) : null}
                </div>
                <div className="nab__col">
                  <button type="button" className={`nab__sort${sortKey === "name" ? " is-sorted" : ""}`} onClick={() => this.toggleSort("name")}>
                    <span>Valve</span><i>{sortKey === "name" && sortDir < 0 ? "↓" : "↑"}</i>
                  </button>
                  <input
                    className="nab__filter"
                    type="text"
                    value={nameQuery}
                    placeholder="Filter valves"
                    aria-label="Filter by valve name"
                    onChange={(e) => this.setState({ nameQuery: e.target.value })}
                  />
                </div>
              </div>
              {list.length ? list.map((v) => {
                const finds = audit ? findingsFor(v, owners, dragId, dragAt) : [];
                return (
                  <div
                    key={v.id}
                    className="nab__vrow"
                    role="button"
                    tabIndex={0}
                    onDoubleClick={() => this.props.onOpenValve && this.props.onOpenValve(v)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") this.props.onOpenValve && this.props.onOpenValve(v);
                    }}
                  >
                    <div className="nab__vzone">{v.zone || "—"}</div>
                    <div className="nab__vname"><span>{v.name || v.id}</span></div>
                    {v.windowed ? <span className="nab__vmode" title="A window, not a shot.">{v.tag || "WINDOW"}</span> : <span />}
                    {finds.length ? <span className="nab__vwarn">⚠ {finds.length}</span> : <span />}
                  </div>
                );
              }) : (
                <div className="nab__empty">
                  {(this.props.valves || []).length
                    ? "No valve matches those filters."
                    : "No valve is on this program yet."}
                </div>
              )}
            </div>
            <div
              className="nab__scroll"
              ref={this.scrollRef}
              onMouseDown={this.onPanDown}
              onContextMenu={(e) => e.preventDefault()}
            >
              <div className="nab__canvas" style={{ width: dayW }}>
                {this.renderMaster(owners, recipes, dayW)}
                <div className="nab__timebar" style={{ width: dayW }}>
                  <GridLines slotW={slotW} hour />
                  {Array.from({ length: 24 }, (_, h) => (
                    <span key={h} className="nab__hour" style={{ left: h * slotW * 2 }}>{shortHour(h)}</span>
                  ))}
                  {Array.from({ length: 24 }, (_, h) => (
                    <i key={`t${h}`} className="nab__tick" style={{ left: h * slotW * 2 + slotW }} />
                  ))}
                </div>
                {list.map((v, i) => (
                  <div key={v.id} className={`nab__lane${i % 2 ? " is-alt" : ""}`} style={{ width: dayW }}>
                    <GridLines slotW={slotW} />
                    {(v.events || []).map((ev, ei) => this.renderBlock(v, ev, ei, owners, recipes))}
                  </div>
                ))}
                {this.props.nowMins != null ? (
                  <div className="nab__now" style={{ left: this.px(this.props.nowMins) }} aria-hidden="true">
                    <span className="nab__nowcap">{fmtTime(Math.floor(this.props.nowMins))}</span>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>

        <div className="nab__legend">
          {legendIds.map((id) => (
            <span key={id} className="nab__lg">
              <i className="nab__sw" style={{ background: colourOf(recipes, id) }} />
              {nameOf(recipes, id)}
            </span>
          ))}
          <span className="nab__lg">
            <i className="nab__sw" style={{ background: "#e4e8ee", border: "1px dashed #b9c2cd" }} />
            No recipe
          </span>
        </div>
      </div>
    );
  }
}
